import * as React from 'react';
import * as Colors from './Colors';
import * as Styles from '@material-ui/core/styles';
import { Grid, TextField } from '@material-ui/core';
import { formatJpPhoneAsYouType } from '../util/phone-util';

// Phone text field with JP format-as-you-type. Reformatting the whole string on each
// keystroke would send the caret to the end, so we preserve it: count the digits before
// the caret, reformat, then place the caret just after the same number of digits.
// Mirrors InputText's title+field grid layout so it drops into the same forms.
const styles = () => Styles.createStyles({
    textField: {
        width: "90%",
        '& > .MuiFormHelperText-contained': { marginLeft: 0, marginRight: 0 },
    },
    input: {
        color: Colors.inputColor,
        padding: "5px",
        "& input": { padding: "5px" }
    },
    row: {
        color: Colors.primaryColor,
        textAlign: "right",
        margin: "auto",
    }
});

interface Props extends Styles.WithStyles {
    title: string,
    value: string,
    onChange: (v: string) => void,
    editable?: boolean,
    error?: boolean,
    helperText?: string
}

class PhoneInput extends React.Component<Props> {
    displayName = "PhoneInput"
    private inputEl: HTMLInputElement | null = null
    private pendingCaret: number | null = null

    // A "significant" char survives formatting and anchors the caret: a digit or a
    // "+". Full-width variants (０-９, ＋) count too — a JP IME emits full-width digits
    // in the raw input while the formatter outputs half-width, so both must be counted
    // or the caret math is off (e.g. jumps to the start). Separators are ignored.
    private isSig(c: string): boolean {
        return (c >= '0' && c <= '9') || (c >= '０' && c <= '９') || c === '+' || c === '＋'
    }

    // Count significant chars in s[0, pos).
    private sigBefore(s: string, pos: number): number {
        let n = 0
        for (let i = 0; i < pos && i < s.length; i++) {
            if (this.isSig(s[i])) n++
        }
        return n
    }

    // Index in s right after `count` significant chars have been seen.
    private caretAfterSig(s: string, count: number): number {
        if (count <= 0) return 0
        let n = 0
        for (let i = 0; i < s.length; i++) {
            if (this.isSig(s[i])) {
                n++
                if (n >= count) return i + 1
            }
        }
        return s.length
    }

    private handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value
        const caret = e.target.selectionStart || 0
        const sig = this.sigBefore(raw, caret)
        const formatted = formatJpPhoneAsYouType(raw)
        this.pendingCaret = this.caretAfterSig(formatted, sig)
        this.props.onChange(formatted)
    }

    public componentDidUpdate() {
        // Restore the caret after the controlled value re-render (once per edit).
        if (this.pendingCaret !== null && this.inputEl) {
            const pos = this.pendingCaret
            this.pendingCaret = null
            try { this.inputEl.setSelectionRange(pos, pos) } catch (e) { /* ignore */ }
        }
    }

    public render() {
        const { classes, title } = this.props
        const canEdit = this.props.editable !== false
        return [
            <Grid key={title + "_1"} item xs={3} className={classes.row}>
                {title}
            </Grid>,
            <Grid key={title + "_2"} item xs={9}>
                {
                    canEdit ?
                        <TextField
                            type="tel"
                            value={this.props.value}
                            onChange={this.handleChange}
                            inputRef={(el) => { this.inputEl = el as HTMLInputElement }}
                            className={classes.textField}
                            error={this.props.error}
                            helperText={this.props.helperText}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ className: classes.input }}
                            margin="none"
                            variant="outlined"
                        />
                        : <div>{this.props.value && this.props.value != "" ? this.props.value : "-"}</div>
                }
            </Grid>
        ]
    }
}

export default Styles.withStyles(styles)(PhoneInput)
